/**
 * 損益カードの判断（#12・`docs/02` §7.1）。★計算はしない。サーバの値を言葉へ写すだけ。
 *
 * ★`null` を 0 円と見せない。理由を必ず添える（未入力／棚卸待ち／固定費が出ない理由）。
 * ★日次と累計の粗利・営業利益には「仕入ベースの概算」と明記する（`docs/02` §7 の実装条件）。
 * ★固定費が出ない理由（`fixedCostReasons`）は行ごとに繰り返さず、注意書きに1回だけ出す。
 */
import type { ComputedAmount, ProfitLoss } from "./api.js";

export interface ProfitLossLine {
  label: string;
  value: string;
}

export interface ProfitLossView {
  /** `note` は見出しの下に1回だけ出す理由（同じ文を行ごとに繰り返さない） */
  blocks: { heading: string; note?: string; lines: ProfitLossLine[] }[];
  warnings: string[];
}

const yen = (n: number): string => `${n.toLocaleString()}円`;
const amount = (a: ComputedAmount, pending: string): string =>
  a.amount === null ? pending : yen(a.amount);
const rate = (r: number | null, pending: string): string =>
  r === null ? pending : `${(r * 100).toFixed(1)}%`;

export const UNRECORDED = "未入力";
export const FIXED_PENDING = "未確定（上の理由）";
export const MONTH_PENDING = "未確定";
export const MONTH_PENDING_NOTE = "月次は、全営業日の売上と期首・期末棚卸が揃うと出ます";

const day = (d: string): string => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;

export function profitLossView(p: ProfitLoss): ProfitLossView {
  const warnings: string[] = [];
  if (p.missingDates.length > 0) {
    // ★カレンダー未整備の月は定休日と見分けが付かず、日付を並べると1か月ぶんの壁になる。日数だけ言う
    warnings.push(
      p.calendarUnavailableReason === null
        ? `売上が未入力の日は累計に入っていません：${p.missingDates.map(day).join("・")}`
        : `売上の入力が無い ${String(p.missingDates.length)}日ぶんは累計に入っていません（カレンダー未整備のため定休日も含みます）`,
    );
  }
  if (p.unconfirmedPurchases > 0) {
    warnings.push(`未承認の仕入 ${yen(p.unconfirmedPurchases)} は、承認するまで原価に入りません`);
  }
  if (p.excludedOtherPurchases > 0) {
    warnings.push(`区分「その他」の仕入 ${yen(p.excludedOtherPurchases)} は原価に入れていません`);
  }
  if (p.pettyExpenseRefunds > 0) {
    warnings.push(`小口現金の経費科目への入金 ${yen(p.pettyExpenseRefunds)} は、固定費から引いていません`);
  }
  if (p.fixedCostReasons.length > 0) {
    warnings.push(`固定費・営業利益・FL率は、まだ計算できません：${p.fixedCostReasons.join("／")}`);
  }
  const fixedPending = p.fixedCostReasons.length > 0 ? FIXED_PENDING : UNRECORDED;
  const num = (n: number | null, pending: string): string => (n === null ? pending : yen(n));
  return {
    blocks: [
      {
        heading: `当日 ${p.date}`,
        lines: [
          { label: "売上（税抜）", value: amount(p.day.salesExclTax, UNRECORDED) },
          { label: "仕入", value: amount(p.day.purchases, UNRECORDED) },
          { label: "粗利（仕入ベースの概算）", value: amount(p.day.grossProfit, UNRECORDED) },
          { label: "固定費（配賦＋人件費＋小口経費）", value: num(p.dayFixedCosts, fixedPending) },
          { label: "営業利益（仕入ベースの概算）", value: amount(p.day.operatingProfit, fixedPending) },
        ],
      },
      {
        heading: `累計 ${p.yearMonth}（${day(p.date)} まで）`,
        lines: [
          { label: "売上（税抜）", value: amount(p.cumulative.salesExclTax, UNRECORDED) },
          { label: "仕入", value: amount(p.cumulative.purchases, UNRECORDED) },
          { label: "粗利（仕入ベースの概算）", value: amount(p.cumulative.grossProfit, UNRECORDED) },
          { label: "固定費（配賦＋人件費＋小口経費）", value: num(p.cumulativeFixedCosts, fixedPending) },
          { label: "営業利益（仕入ベースの概算）", value: amount(p.cumulative.operatingProfit, fixedPending) },
        ],
      },
      {
        heading: `月次 ${p.yearMonth}（棚卸調整後）`,
        ...(p.month.grossProfit.amount === null ? { note: MONTH_PENDING_NOTE } : {}),
        lines: [
          { label: "原価", value: amount(p.month.cost, MONTH_PENDING) },
          { label: "原価率", value: rate(p.month.costRate, MONTH_PENDING) },
          { label: "粗利", value: amount(p.month.grossProfit, MONTH_PENDING) },
          { label: "粗利率", value: rate(p.month.grossProfitRate, MONTH_PENDING) },
          { label: "人件費", value: num(p.month.laborCost, fixedPending) },
          { label: "人件費率", value: rate(p.month.laborCostRate, p.month.laborCost === null ? fixedPending : MONTH_PENDING) },
          { label: "FL率", value: rate(p.month.flRate, p.month.laborCost === null ? fixedPending : MONTH_PENDING) },
          { label: "固定費", value: num(p.month.fixedCosts, fixedPending) },
          { label: "営業利益", value: amount(p.month.operatingProfit, p.month.fixedCosts === null ? fixedPending : MONTH_PENDING) },
          { label: "営業利益率", value: rate(p.month.operatingProfitRate, p.month.fixedCosts === null ? fixedPending : MONTH_PENDING) },
        ],
      },
    ],
    warnings,
  };
}

export interface TrendBar {
  /** `9/5` */
  day: string;
  /** 読み上げ・ホバー・表に出す文（`9/5 売上 92,000円`／未入力なら `—`） */
  value: string;
  /** 棒の高さ。最大の絶対値に対する割合（0〜1）。値が無ければ `null`＝棒を描かない */
  ratio: number | null;
  negative: boolean;
}

export interface TrendChart {
  title: string;
  bars: TrendBar[];
  /** 赤字の日があるか。あれば基線を真ん中へ置く */
  hasNegative: boolean;
  /** 1日も値が無い（固定費が未確定の月の営業利益など）。棒の枠を描かず、この文だけ出す */
  empty: string | null;
}

/**
 * 推移（`docs/02` §7.1）。★計算はしない。日別の値を棒の高さと文へ写すだけ。
 * ★売上と営業利益は**別の2枚**にする（1枚に2つの目盛りを持たせない）。
 * ★`null` は棒を描かず「—」。0 円と見せない。
 */
export function trendCharts(trend: ProfitLoss["trend"]): TrendChart[] {
  const chart = (title: string, pick: (t: ProfitLoss["trend"][number]) => number | null): TrendChart => {
    const max = Math.max(0, ...trend.map((t) => Math.abs(pick(t) ?? 0)));
    return {
      title,
      empty: trend.every((t) => pick(t) === null) ? `${title}：計算できる日がまだありません` : null,
      hasNegative: trend.some((t) => (pick(t) ?? 0) < 0),
      bars: trend.map((t) => {
        const v = pick(t);
        return {
          day: day(t.date),
          value: v === null ? "—" : yen(v),
          ratio: v === null ? null : max === 0 ? 0 : Math.abs(v) / max,
          negative: v !== null && v < 0,
        };
      }),
    };
  };
  return [
    chart("売上（税抜）", (t) => t.salesExclTax),
    chart("営業利益（仕入ベースの概算）", (t) => t.operatingProfit),
  ];
}
